import { useEffect, useRef, useState } from "react";

export const useDynamicLayoutHeight = (options = {}) => {
  /**
   * The height is calculated based on the parent wrapper's top offset from
   * the top of the viewport: height = 100dvh - parent.top - offset.
   *
   * Options:
   *  - offset: extra pixels to subtract (e.g. parent is inside a modal with
   *    its own bottom offset).
   *
   *  - observeLayoutChanges: when true, re-measures on any layout change
   *    above/around the parent, using a ResizeObserver on
   *    document.documentElement plus a window resize listener. Defaults to
   *    false to preserve the original one-shot measurement behavior.
   *
   *    Why this exists: the default measurement runs once inside a useEffect
   *    on mount. If content above the parent (page header, breadcrumbs,
   *    totals bar, etc.) grows AFTER that measurement — typically because it
   *    depends on data from an async query that hasn't resolved yet — the
   *    captured top offset stays too small and the computed height ends up
   *    too large, overflowing the viewport.
   *
   *    Concrete case that motivated this option: SM_PublishedListing renders
   *    a <Breadcrumbs/> whose final crumb's display comes from a pending
   *    `useGetOnePublishedOrUnpublishedTpl` query. On first mount the crumb
   *    text is empty, the breadcrumb bar is shorter, and the downstream
   *    parent's top is ~24px higher than its eventual resting position.
   *    Without a re-measurement, the dynamic height over-shoots by exactly
   *    that delta.
   *
   *    Enable this flag whenever the parent lives below any async-driven
   *    layout (breadcrumbs, auto-translated titles, lazy headers, etc.).
   */
  const { offset = 0, observeLayoutChanges = false } = options;
  const parentWrapperRef = useRef();

  const [gridHeightCSS, setGridHeightCSS] = useState("100dvh");

  useEffect(() => {
    let lastTop = -1;
    const measure = () => {
      const top = parentWrapperRef.current?.getBoundingClientRect()?.top || 0;
      // No-op when nothing meaningful changed — avoids redundant React state
      // updates on every observed mutation/frame.
      if (top === lastTop) return;
      lastTop = top;
      setGridHeightCSS(`calc(100dvh - ${top + offset}px)`);
    };

    measure();

    if (!observeLayoutChanges) return;

    // The parent's *position* shifts when content above it grows, but neither
    // its own size nor the viewport's size changes — so a simple
    // ResizeObserver on documentElement misses these cases (e.g. the
    // breadcrumb-content-loads-late scenario in SM_PublishedListing).
    //
    // Strategy: observe DOM mutations on the body (catches async content
    // arriving anywhere upstream) AND ResizeObserver on body / documentElement
    // (catches viewport + body box changes), all funnelled through a single
    // rAF-debounced re-measurement. We early-out when top hasn't actually
    // changed, so the cost stays low.
    let rafId = null;
    const schedule = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        measure();
      });
    };

    const ro = new ResizeObserver(schedule);
    ro.observe(document.documentElement);
    ro.observe(document.body);

    const mo = new MutationObserver(schedule);
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
    });

    window.addEventListener("resize", schedule);
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener("resize", schedule);
    };
  }, [offset, observeLayoutChanges]);

  return { parentWrapperRef, gridHeightCSS };
};
